import {OperationVariables} from '@apollo/client/core';
import {gql} from 'apollo-angular';
import {DocumentNode, FragmentDefinitionNode, FragmentSpreadNode, print, SelectionSetNode} from 'graphql';

interface FragmentMapValue {
    name: string;
    fragment: string;
    deps: string[];
    raw: FragmentDefinitionNode;
}

const walkDef = (node: any, knownFragments = new Set()): Set<any> => {
    if (node.kind === 'FragmentSpread') {
        knownFragments.add((<FragmentSpreadNode>node).name.value);
    }

    if (Array.isArray((<SelectionSetNode>node).selections)) {
        node.selections.forEach(childNode => walkDef(childNode, knownFragments));
    }

    if (node.selectionSet) {
        node.selectionSet.selections.forEach(childNode => walkDef(childNode, knownFragments));
    }

    return knownFragments;
};

const checkCircularDeps = (fragment: FragmentMapValue, all: Map<string, FragmentMapValue>) => {
    if (!fragment.deps.length) {
        return false;
    }

    const stack = [];

    const walkDeps = (fr: FragmentMapValue, parentDeps = {}) => {
        stack.push(fr.name);
        const localDeps = {...parentDeps, [fr.name]: true};
        if (fr.deps.some(dep => localDeps[dep])) {
            throw new Error(`Fragments Circular dependency found! ${stack.join(' -> ')}`);
        }
        fr.deps.forEach(name => walkDeps(all.get(name), localDeps));
        stack.splice(-1, 1);
    };

    return walkDeps(fragment, {
        [fragment.name]: true,
    });
};

export class FragmentHelper {
    private knownFragments = new Map<string, FragmentMapValue>();

    private getFragmentWithDeps(fragmentName: string, resultSet: Set<string>) {
        resultSet.add(fragmentName);
        const fragment = this.knownFragments.get(fragmentName);
        if (!fragment) {
            throw new Error(`Fragment with name '${fragmentName}' is not registered!
            Do use registerFragments method to register it.
            Example:

            const appAttributesFragment = gql\`
                fragment appAttributes on App {
                    activeAdTypes
                    id
                    name
                    platform
                    permissions {
                        canViewConfiguration
                    }
                }
            \`;
            // where api is instance of ApiService
            this.api.registerFragments(appAttributesFragment);

            `);
        }
        fragment.deps.forEach(dep => resultSet.add(dep));
    }

    private patchDocumentNode(query: DocumentNode) {
        const usedFragments = new Set<any>();
        query.definitions.forEach((node: any) => walkDef(node, usedFragments));
        if (!usedFragments.size) {
            // this query/mutation doesnt use fragments it is not supposed to be patched
            return query;
        }
        // clone it to be on the save side
        [...usedFragments].forEach(fragment => this.getFragmentWithDeps(fragment, usedFragments));
        const resultFragments = [...usedFragments].map(name => this.knownFragments.get(name).fragment).join('\n');

        return gql([query.loc.source.body, resultFragments].join('\n'));
    }

    injectKnownFragments<T extends OperationVariables>(options, requestName): T {
        return {...options, [requestName]: this.patchDocumentNode(options[requestName])};
    }

    registerFragments(fragments: DocumentNode) {
        let newFragments = [];
        let knownFragments = new Map(this.knownFragments);
        fragments.definitions.forEach((fr: FragmentDefinitionNode) => {
            if (!knownFragments.has(fr.name.value)) {
                newFragments.push(fr.name.value);
                knownFragments.set(fr.name.value, {
                    name: fr.name.value,
                    fragment: print(fr),
                    deps: [...walkDef(fr)],
                    raw: fr,
                });
            }
        });
        // check deps for new fragments
        newFragments.forEach(name => {
            checkCircularDeps(knownFragments.get(name), knownFragments);
        });
        this.knownFragments = knownFragments;
    }
}
